// AdminPanel.js
import React, { useState, useEffect } from 'react';
import axios from '../../api/axiosInstance';
import styles from './AdminPanel.module.css'; // Używamy CSS Modules
import MapEditor from './MapEditor';
import showModal from '../ui/modals/showModal';

const HOST = process.env.REACT_APP_HOST;
const PORT = process.env.REACT_APP_API_PORT;
const baseUrl = `https://${HOST}`;
 
/**
 * Główny komponent panelu administratora.
 * Obsługuje cztery zakładki:
 * - Użytkownicy
 * - Rezerwacje
 * - Edycja mapy
 * - Raporty
 */

function AdminPanel() {
   // 🧾 Zakładki
  const [tab, setTab] = useState('users');
  const [editingUser, setEditingUser] = useState(null);

  // 👥 Lista użytkowników i formularz dodawania
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', role: 'user', regCarNum: ['']});
  const [searchTerm, setSearchTerm] = useState('');

    // 🔎 Filtrowanie użytkowników
  const filteredUsers = users.filter(u =>
    u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (Array.isArray(u.regCarNum) &&
    u.regCarNum.some(num =>
      num.toLowerCase().includes(searchTerm.toLowerCase())
    ))
  );

  // 🔄 Pobieranie listy użytkowników z serwera
  const fetchUsers = async () => {
    const res = await axios.get(`${baseUrl}/api/admin/auth/users`);
    const sortedUsers = res.data.sort((a, b) => a.username.localeCompare(b.username)); // Sortowanie A-Z
    setUsers(sortedUsers);
  };

  // ➕ Tworzenie nowego użytkownika
  const createUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.password) {
      return showModal("Uzupełnij wszystkie pola.", {type: "info"});
    }
  
    const payload = {
      ...newUser,
      regCarNum: newUser.regCarNum.filter(Boolean),
    };
  
    try {
      await axios.post(`${baseUrl}/api/admin/create-user`, payload);
      setNewUser({ username: '', email: '', password: '', role: 'user', regCarNum: [''] });
      fetchUsers();
    } catch (err) {
      if (err.response?.data?.errors) {
        const messages = err.response.data.errors.map(e => `• ${e.message}`).join('\n');
        showModal(`Błąd walidacji:\n${messages}`, { type: "error" });
      } else {
        showModal(err.response?.data?.message || 'Nieznany błąd serwera.', { type: 'error' });
      }
    }
  };

 // ❌ Usuwanie użytkownika
 const deleteUser = async (id) => {
  const token = localStorage.getItem('token');
  if (!token) return showModal('Brak autoryzacji. Zaloguj się ponownie.', { type: 'error' });

  await axios.delete(`${baseUrl}/api/admin/auth/users/${id}`);
  fetchUsers();
};

  // 📆 Rezerwacje użytkownika
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [reservations, setReservations] = useState([]);

  const fetchUserReservations = async (userId) => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${baseUrl}/api/admin/reservations/${userId}`);
    setReservations(res.data);
  };

  const deleteReservation = async (id) => {
    const token = localStorage.getItem('token');
    await axios.delete(`${baseUrl}/api/reservations/${id}`);
    if (selectedUserId) fetchUserReservations(selectedUserId);
  };

// 📊 Raport miesięczny
  const [month, setMonth] = useState('');
  const [report, setReport] = useState(null);
  const [userReport, setUserReport] = useState([]);

  const generateReport = async () => {
    if (!month) return showModal('Wybierz miesiąc', {type: "info"});
    const [year, monthNumber] = month.split('-');
    const startDate = `${year}-${monthNumber}-01`;
    const endDate = new Date(year, monthNumber, 0).toISOString().split('T')[0];
    const res = await axios.get(`${baseUrl}/api/admin/report?startDate=${startDate}&endDate=${endDate}`);
    const data = res.data;

    const counts = {};
    data.forEach(r => {
      if (!counts[r.date]) counts[r.date] = 0;
      counts[r.date]++;
    });

    const totalDays = Object.keys(counts).length;
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    const avg = totalDays ? (total / totalDays).toFixed(2) : 0;

    setReport({ counts, avg });
  };
  const generateUserReport = async () => {
    try {
      const res = await axios.get(`${baseUrl}/api/admin/auth/users`);
      const sorted = res.data
        .map(u => ({
          username: u.username,
          regCarNum: Array.isArray(u.regCarNum) && u.regCarNum.length > 0
            ? u.regCarNum.join(', ')
            : ''
        }))
        .sort((a, b) => {
          const aHasCars = a.regCarNum !== '';
          const bHasCars = b.regCarNum !== '';

          if (aHasCars && !bHasCars) return -1;
          if (!aHasCars && bHasCars) return 1;

          return a.username.localeCompare(b.username);
        });

      setUserReport(sorted);
    } catch (err) {
      showModal('Błąd podczas generowania raportu użytkowników', { type: 'error' });
    }
  };
    // Funkcja generująca CSV z userReport
  const downloadUserCSV = async () => {
    try {
      const res = await axios.get(`${baseUrl}/api/admin/auth/users`);
      const users = res.data;

      const headers = [
        'Nazwa użytkownika',
        'Email',
        'Numer rejestracyjny',
        'Rola',
        'Typ konta'
      ];

      const rows = users.map(u => [
        `"${u.username}"`,
        `"${u.email}"`,
        `"${Array.isArray(u.regCarNum) ? u.regCarNum.filter(Boolean).join(';') : u.regCarNum || ''}"`,
        `"${u.role}"`,
        `"${u.loginMethod || ''}"`
      ]);

      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
      const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const today = new Date().toISOString().slice(0, 10); // yyyy-mm-dd
      const filename = `uzytkownicy_parkingapp_${today}.csv`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      showModal('Błąd pobierania użytkowników do CSV', { type: 'error' });
    }
  };


  // 📥 Pobranie użytkowników przy wejściu na zakładkę
  useEffect(() => {
    if (tab === 'users') fetchUsers();
  }, [tab]);

  return (
    <div className={styles.container}>
      <h2>Panel Administratora</h2>
      <div className={styles.tabs}>
        <button
          className={`${styles.tabButton} ${tab === 'users' ? styles.tabButtonActive : ''}`}
          onClick={() => setTab('users')}
        >
          Użytkownicy
        </button>
        <button
          className={`${styles.tabButton} ${tab === 'reservations' ? styles.tabButtonActive : ''}`}
          onClick={() => setTab('reservations')}
        >
          Rezerwacje
        </button>
        <button
          className={`${styles.tabButton} ${tab === 'map' ? styles.tabButtonActive : ''}`}
          onClick={() => setTab('map')}
        >
          🛠 Edycja mapy miejsc
        </button>
        <button
          className={`${styles.tabButton} ${tab === 'reports' ? styles.tabButtonActive : ''}`}
          onClick={() => setTab('reports')}
        >
          Raporty
        </button>
      </div>

      <div className={styles.tabContent}>
        {tab === 'users' && (
          <div>
            <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Lista użytkowników</span>
              <input
                className={styles.input}
                placeholder="Szukaj użytkownika..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ maxWidth: '300px' }}
              />
            </h3>
            <div className={styles.usersTableWrapper}>
              <table className={styles.usersTable}>
                <thead>
                  <tr>
                    <th>Nazwa użytkownika</th>
                    <th>Email</th>
                    <th>Numer rejestracyjny</th>
                    <th>Rola</th>
                    <th>Akcja</th>
                  </tr>
                </thead>
                <tbody>
                {filteredUsers.map(u => (
                  <tr key={u.id}>
                    <td>{u.username}</td>
                    <td>{u.email}</td>
                    <td title={u.regCarNum || '--'}>
                      {u.regCarNum+""|| '--'}
                    </td>
                    <td>{u.role}</td>
                    <td>
                    <button className={`${styles.actionButton} ${styles.iconButton} ${styles.iconButtonEdit}`} onClick={() => setEditingUser(u)}>Edytuj</button>
                    <button className={`${styles.actionButtondel} ${styles.iconButton} ${styles.iconButtonDelete}`} onClick={() => deleteUser(u.id)}>Usuń</button>
                  </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
            {editingUser && (
              <div style={{ marginTop: '2rem' }}>
                <h4>Edytuj użytkownika: {editingUser.username}</h4>
                <input
                  className={styles.input}
                  placeholder="Nazwa"
                  value={editingUser.username}
                  onChange={e => setEditingUser({ ...editingUser, username: e.target.value })}
                />
                <input
                  className={styles.input}
                  placeholder="Email"
                  value={editingUser.email}
                  onChange={e => setEditingUser({ ...editingUser, email: e.target.value })}
                />
                <select
                  className={styles.select}
                  value={editingUser.role}
                  onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}
                >
                  <option value="user">Użytkownik</option>
                  <option value="admin">Administrator</option>
                </select>
                  {editingUser.loginMethod !== 'microsoft' ? (
                    <>
                      <input
                        className={styles.input}
                        type="password"
                        placeholder="Nowe hasło"
                        value={editingUser.password || ''}
                        onChange={e => setEditingUser({ ...editingUser, password: e.target.value })}
                      />
                      <small style={{ fontSize: '0.85rem', color: '#666' }}>
                        Hasło musi mieć min. 14 znaków, zawierać duże i małe litery, cyfrę i znak specjalny.
                      </small>
                    </>
                  ) : (
                    <p style={{ fontSize: '0.9rem', color: '#999' }}>
                      Użytkownik loguje się przez Microsoft – hasło nie może być zmienione.
                    </p>
                  )}
                <br/>
                <label><strong>Numery rejestracyjne:</strong></label>
                  {editingUser.regCarNum.map((num, index) => (
                    <div key={index} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <input
                        className={styles.input}
                        value={num}
                        onChange={e => {
                          const updated = [...editingUser.regCarNum];
                          updated[index] = e.target.value;
                          setEditingUser({ ...editingUser, regCarNum: updated });
                        }}
                      />
                      <button
                        type="button"
                        className={styles.actionButton}
                        style={{ backgroundColor: '#ddd' }}
                        onClick={() => {
                          const updated = editingUser.regCarNum.filter((_, i) => i !== index);
                          setEditingUser({ ...editingUser, regCarNum: updated });
                        }}
                      >🗑️</button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className={styles.actionButton}
                    onClick={() => setEditingUser({ ...editingUser, regCarNum: [...editingUser.regCarNum, ""] })}
                  >
                    + Dodaj numer
                  </button>
                  <br/>
                <button
                  className={styles.actionButton}
                  onClick={async () => {
                    const payload = {
                      ...editingUser,
                      regCarNum: editingUser.regCarNum.filter(Boolean)
                    };

                    // nie wysyłaj pustego hasła (opcjonalne bezpieczeństwo)
                    if (!payload.password) {
                      delete payload.password;
                    }
                    try {
                      await axios.put(`${baseUrl}/api/admin/auth/users/${editingUser.id}`, payload);
                      setEditingUser(null);
                      fetchUsers();
                    } catch (err) {
                      if (err.response?.data?.errors) {
                        const messages = err.response.data.errors.map(e => `• ${e.message}`).join('\n');
                        showModal(`Błąd walidacji:\n${messages}`, { type: "error" });
                      } else {
                        showModal(err.response?.data?.message || 'Błąd aktualizacji użytkownika.', { type: 'error' });
                      }
                    }
                  }}
                >
                  💾 Zapisz zmiany
                </button>
                <button
                  className={styles.actionButton}
                  style={{ backgroundColor: '#999', marginLeft: '1rem' }}
                  onClick={() => setEditingUser(null)}
                >
                  ❌ Anuluj
                </button>
              </div>
            )}
            <br />
            <h4>Nowy użytkownik</h4>
            <input
              className={styles.input}
              placeholder="Nazwa"
              value={newUser.username}
              onChange={e => setNewUser({ ...newUser, username: e.target.value })}
            />
            <input
              className={styles.input}
              placeholder="Email domenowy"
              value={newUser.email}
              onChange={e => setNewUser({ ...newUser, email: e.target.value })}
            />
            <input
              className={styles.input}
              placeholder="Hasło"
              value={newUser.password}
              onChange={e => setNewUser({ ...newUser, password: e.target.value })}
            />
            <select
              className={styles.select}
              value={newUser.role}
              onChange={e => setNewUser({ ...newUser, role: e.target.value })}
            >
              <option value="user">Użytkownik</option>
              <option value="admin">Administrator</option>
            </select>
            <label><strong>Numery rejestracyjne:</strong></label>
              {newUser.regCarNum.map((num, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <input
                    className={styles.input}
                    value={num}
                    onChange={e => {
                      const updated = [...newUser.regCarNum];
                      updated[index] = e.target.value;
                      setNewUser({ ...newUser, regCarNum: updated });
                    }}
                  />
                  <button
                    type="button"
                    className={styles.actionButton}
                    style={{ backgroundColor: '#ddd' }}
                    onClick={() => {
                      const updated = newUser.regCarNum.filter((_, i) => i !== index);
                      setNewUser({ ...newUser, regCarNum: updated });
                    }}
                  >🗑️</button>
                </div>
              ))}
              <button
                type="button"
                className={styles.actionButton}
                onClick={() => setNewUser({ ...newUser, regCarNum: [...newUser.regCarNum, ''] })}
              >
                + Dodaj numer
              </button>

            <button className={styles.actionButton} onClick={createUser}>Utwórz</button>
            <br/>
            <small style={{ fontSize: '0.85rem', color: '#666' }}>
              Hasło musi mieć min. 14 znaków, zawierać duże i małe litery, cyfrę i znak specjalny.
            </small>
          </div>
        )}

        {tab === 'reservations' && (
          <div>
            <h3>Rezerwacje użytkownika</h3>
            <select
              className={styles.select}
              onChange={(e) => {
                setSelectedUserId(e.target.value);
                fetchUserReservations(e.target.value);
              }}
            >
              <option>-- wybierz użytkownika --</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.username}</option>
              ))}
            </select>

            {reservations.length > 0 && (
              <table className={styles.reservationsTable}>
                <thead>
                  <tr><th>Data</th><th>Miejsce</th><th></th></tr>
                </thead>
                <tbody>
                  {reservations.map(r => (
                    <tr key={r.id}>
                      <td>{r.date}</td>
                      <td>{r.ParkingSpot?.spotNumber}</td>
                      <td><button className={styles.actionButton} onClick={() => deleteReservation(r.id)}>Usuń</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        
        {tab === 'map' && <MapEditor />}

        {tab === 'reports' && (
          <div>
            <div>
            <h3>Raport miesięczny</h3>
            <input
              className={styles.input}
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
            <button className={styles.actionButton} onClick={generateReport}>Generuj raport miesięczny</button>
            
            {report && (
              <div>
                <h4>Szczegóły:</h4>
                <ul>
                  {Object.entries(report.counts).map(([date, count]) => (
                    <li key={date}>{date}: {count} zajęte miejsca</li>
                  ))}
                </ul>
                <p><strong>Średnia zajętość:</strong> {report.avg}</p>
              </div>
            )}
            </div>
            <div>
            <h4>Raport użytkowników</h4>
            <button className={styles.actionButton} onClick={generateUserReport}>Generuj raport użytkowników</button>
            <button
              className={styles.actionButton}
              style={{ marginLeft: '1rem' }}
              onClick={downloadUserCSV}
            >
              Pobierz CSV
            </button>
            {userReport.length > 0 && (
              <table className={styles.spotsTable}>
                <thead>
                  <tr>
                    <th>Użytkownik</th>
                    <th>Numery rejestracyjne</th>
                  </tr>
                </thead>
                <tbody>
                  {userReport.map((u, idx) => (
                    <tr key={idx}>
                      <td>{u.username}</td>
                      <td>{u.regCarNum || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
